/**
 * Variable NAMES, and the four booleans around a name. **Never a value, never a
 * length, never a prefix, never a hash. O7 is untouched by this module and by
 * this phase** (O28 decided the names half, and only the names half).
 *
 * ## Why names are safe and why the feature is impossible without them
 *
 * The app already draws this exact line and says why — the AI's pre-request dry
 * run reports *"whether the script compiled and ran, plus the NAMES (never the
 * values) of the variables it would set"* (`toolSchemas.js:244`), and
 * `utils/scriptVariables.js:22-31` gives `secret: true` its one meaning: the
 * value is *"for substitution into a sent request and nothing else"*.
 *
 * And without names the whole read side is inert. `request_read` returns
 * `Authorization: Bearer {{apiToken}}` — a pointer. A pointer to a namespace
 * nobody may enumerate is a pointer to nothing, and *"why does this request
 * return 401"* becomes unanswerable. The name is ALREADY visible in the header
 * value we return; withholding the LIST of names would protect nothing and
 * break the feature.
 *
 * ## What is deliberately not here even as a name
 *
 *   * the **session overlay** (`utils/scriptEnvOverlay.js`) — deliberately
 *     non-durable and cleared at sign-out. A name that exists only inside one
 *     send is not a property of the user's workspace.
 *   * **script-local** variables, for the same reason.
 *   * **dynamic** variables (`$randomUUID` and friends) — computed, not stored,
 *     and a model that treats one as unset would be wrong.
 */
import { useVariablesStore } from '../store/variablesStore';
import { folderVariablesFor } from '../utils/folderVariables';
import { SCOPE_PRECEDENCE } from '../utils/variablePrecedence';

/**
 * Precedence, highest first — THE order (utils/variablePrecedence): the
 * request's folder chain (nearest first), then environment, THEN collection.
 * It is the shared list itself, never a copy: a copy here once disagreed with
 * the resolver, and `definedIn` named the wrong scope whenever a key was in
 * both. Environment above collection since 16.10.2026 (as in Postman).
 */
export const SCOPE_ORDER = SCOPE_PRECEDENCE;

function rowsOf(list) {
  return (Array.isArray(list) ? list : []).filter((v) => v && String(v.key || '').trim());
}

/**
 * One entry per variable name: where it is defined, whether the user marked it
 * secret, and whether it is empty. `isEmpty` is derived from the value WITHOUT
 * copying it — it is the single most useful fact about a variable for a
 * diagnosis and it discloses one bit.
 */
export function variableIndex({ collectionId, collection = null, folderId = null } = {}) {
  const state = useVariablesStore.getState();
  const byName = new Map();

  const add = (row, scope, scopeName) => {
    const name = String(row.key).trim();
    const value = typeof row.value === 'string' ? row.value : '';
    const existing = byName.get(name);
    const entry = existing || {
      name,
      definedIn: null,
      definedInName: null,
      isSecret: false,
      isEmpty: true,
      enabled: true,
      scopes: [],
    };
    entry.scopes.push(scope);
    entry.isSecret = entry.isSecret || row.secret === true;
    // The highest-precedence definition wins, and SCOPE_ORDER is walked in
    // that order below, so the first one seen is the effective one.
    if (entry.definedIn === null) {
      entry.definedIn = scope;
      entry.definedInName = scopeName;
      entry.isEmpty = value.length === 0;
      entry.enabled = row.enabled !== false;
    }
    byName.set(name, entry);
  };

  // Walked in SCOPE_ORDER. Folder rows come nearest folder first, so the
  // nearest definition is the one `add` keeps; `definedInName` is its folder.
  // A DISABLED folder row does not win: Send falls through it to the next
  // scope, so naming it first would diagnose "disabled" for a value that does
  // substitute (tester F6). It is only recorded after every other scope, so a
  // key defined ONLY by a switched-off folder row still reads as "disabled"
  // rather than "undefined".
  const folderRows = collection && folderId != null
    ? rowsOf(folderVariablesFor(collection, folderId)).map((row) => [row, String(row.folderName || '')])
    : [];
  const sources = {
    folder: () => folderRows.filter(([row]) => row.enabled !== false),
    collection: () => (collectionId != null
      ? rowsOf((state.collectionVariablesById || {})[String(collectionId)]).map((row) => [row, String(collectionId)])
      : []),
    environment: () => rowsOf(state.selectedEnvironment?.variables)
      .map((row) => [row, String(state.selectedEnvironment?.name || '')]),
    workspace: () => rowsOf(state.workspaceVariables).map((row) => [row, null]),
    global: () => rowsOf(state.globalVariables).map((row) => [row, null]),
  };
  for (const scope of SCOPE_ORDER) {
    for (const [row, scopeName] of sources[scope]()) add(row, scope, scopeName);
  }
  for (const [row, scopeName] of folderRows) {
    if (row.enabled === false) add(row, 'folder', scopeName);
  }

  return byName;
}

/** The selected environment, by id and name. Never its variables' values. */
export function selectedEnvironmentReport() {
  const env = useVariablesStore.getState().selectedEnvironment;
  if (!env) return null;
  return { id: String(env.id ?? ''), name: String(env.name || '') };
}

/**
 * Every environment, and the two variable sets that are not environments, as
 * NAMES with their flags. This is what `environments_list` answers with.
 */
export function environmentsReport() {
  const state = useVariablesStore.getState();
  const selectedId = state.selectedEnvironment?.id ?? null;
  const project = (rows) =>
    rowsOf(rows).map((row) => ({
      name: String(row.key).trim(),
      isSecret: row.secret === true,
      isEmpty: !(typeof row.value === 'string' && row.value.length > 0),
      enabled: row.enabled !== false,
    }));

  return {
    selectedEnvironmentId: selectedId == null ? null : String(selectedId),
    environments: (Array.isArray(state.environments) ? state.environments : []).map((env) => ({
      id: String(env?.id ?? ''),
      name: String(env?.name || ''),
      selected: selectedId != null && String(env?.id) === String(selectedId),
      variables: project(env?.variables),
    })),
    workspaceVariables: project(state.workspaceVariables),
    globalVariables: project(state.globalVariables),
  };
}
